/**
 * ReportingNowBadge — the small circular "R" in the narrow live-report-status
 * column, showing that a study is open in the report editor right now.
 *
 * Three states, because the lock no longer necessarily belongs to the row it is
 * drawn on. How far a lock reaches is configured per center (Settings > Report
 * Configuration > Reporting Lock):
 *
 *   violet  someone else has THIS study open        → they hold it, you cannot open it
 *   amber   someone else has ANOTHER study of this   → the patient (or the order) is
 *           patient / order open                       locked through a sibling study
 *   slate   YOU hold the lock (either way)          → informational only, never blocks you
 *
 * The colour answers "can I open this?" at a glance; the tooltip says who and,
 * for a sibling lock, which accession they are actually on.
 *
 * Renders nothing when nobody holds the study, keeping the column empty.
 */
import React from 'react';
import { Tooltip } from 'antd';

export interface ReportingNowLock {
  active?: boolean;
  /** true when the lock is on this very order, false when inherited from a sibling */
  own?: boolean;
  /** true when the current user is the holder */
  self?: boolean;
  scope?: string;
  /** holder's username */
  by?: string | null;
  /** holder's full name, resolved server-side */
  byName?: string | null;
  /** accession of the study actually being reported (for a sibling lock) */
  acc?: string | null;
}

// Top-lit radial gradient for the "bump", an inset white highlight along the top
// plus an inset dark edge at the bottom for depth, and a soft drop shadow.
const ORBS = {
  own: {
    background: 'radial-gradient(circle at 50% 30%, #B488F9 0%, #7C3AED 55%, #5B21B6 100%)',
    boxShadow:
      '0 1px 2px rgba(76, 29, 149, 0.45), inset 0 1px 1.5px rgba(255, 255, 255, 0.6), inset 0 -1.5px 2px rgba(76, 29, 149, 0.55)',
  },
  sibling: {
    background: 'radial-gradient(circle at 50% 30%, #FCD34D 0%, #F59E0B 55%, #B45309 100%)',
    boxShadow:
      '0 1px 2px rgba(146, 64, 14, 0.45), inset 0 1px 1.5px rgba(255, 255, 255, 0.65), inset 0 -1.5px 2px rgba(146, 64, 14, 0.55)',
  },
  self: {
    background: 'radial-gradient(circle at 50% 30%, #E2E8F0 0%, #94A3B8 55%, #64748B 100%)',
    boxShadow:
      '0 1px 2px rgba(71, 85, 105, 0.35), inset 0 1px 1.5px rgba(255, 255, 255, 0.7), inset 0 -1.5px 2px rgba(71, 85, 105, 0.45)',
  },
};

export const ReportingNowBadge: React.FC<{
  lock?: ReportingNowLock | null;
}> = ({ lock }) => {
  if (!lock?.active) return null;

  const name = (lock.byName || '').trim();
  const username = (lock.by || '').trim();
  const who = name && username ? `${name} (${username})` : name || username || 'Another user';
  const acc = (lock.acc || '').trim();

  let tooltipTitle: string;
  if (lock.self) {
    tooltipTitle = lock.own
      ? 'You have this study open for reporting'
      : `You are reporting ${acc || 'another study'} for this patient`;
  } else if (lock.own) {
    tooltipTitle = `${who} is reporting this study`;
  } else {
    tooltipTitle = acc
      ? `${who} is reporting ${acc} for this patient`
      : `${who} is reporting another study of this patient`;
  }

  const orb = lock.self ? ORBS.self : lock.own ? ORBS.own : ORBS.sibling;

  return (
    <Tooltip title={tooltipTitle}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          ...orb,
          color: '#fff',
          textShadow: '0 1px 1px rgba(0, 0, 0, 0.3)',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '11px',
          lineHeight: '20px',
          letterSpacing: 0,
        }}
      >
        R
      </span>
    </Tooltip>
  );
};

export default ReportingNowBadge;
